import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import UploadZone from './components/UploadZone';
import Editor from './components/Editor';
import ResultViewer from './components/ResultViewer';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import BuyCreditsModal from './components/BuyCreditsModal';
import AdminPanel from './components/AdminPanel';
import ReferralsPage from './components/ReferralsPage';
import { ReferralPromoPopup } from '@rehub/announcements';
import EmbedPage from './components/EmbedPage';
import LandingPage from './components/LandingPage';
import FluidBackground from './components/FluidBackground';
import PricingBenefitsModal from './components/PricingBenefitsModal';
import ModeSelection from './components/ModeSelection';
import ProOrderModal, { ProOrderData } from './components/ProOrderModal';
import BrandingModal from './components/BrandingModal';
import { AppState, ImageState, WatermarkSettings, UserProfile, GenerationMode, VectorFloorPlan, BrandingProfile, DEFAULT_BRANDING_PROFILE } from './types';
import { declutterImage, upscaleImage, extractFloorPlanVectors, convertFloorPlanWithReference } from './services/gemini';
import { supabase, isConfigured, uploadImageToStorage, refundCredit, getAdminRole, logEvent, checkUserGenerationPermission, createGenerationRecord, updateGenerationRecord } from './services/supabase';
import { Loader2, Sparkles, X, LogIn, UserPlus, Image as ImageIcon, CheckCircle, AlertCircle } from 'lucide-react';
import { Session } from '@supabase/supabase-js';
import { AUTO_DECLUTTER_PROMPT, APP_VERSION } from './constants';
import DirectCardPricingTable from './components/DirectCardPricingTable';
import GuestOrderFlow, { loadDraft } from './components/GuestOrderFlow';
import OrderConfirmed from './components/OrderConfirmed';
import { trackSketchplan } from './lib/analytics';
import { orderPath, parseSketchplanEntry, SKETCHPLAN_LOGIN_PATH } from './lib/entry';
import type { SketchplanCurrency, SketchplanPropertyType } from '@rehub/pricing';

const cropResultToOriginal = async (base64Result: string, restoreRect: {x: number, y: number, width: number, height: number} | null, is360: boolean = false): Promise<string> => {
  if (!restoreRect) return base64Result;
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      
      // Calculate crop dimensions with rounding for pixel precision
      const targetW = Math.round(img.width * restoreRect.width);
      const targetH = Math.round(img.height * restoreRect.height);
      const startX = Math.round(img.width * restoreRect.x);
      const startY = Math.round(img.height * restoreRect.y);

      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject("Canvas context error");
        return;
      }
      
      // Use high-quality image rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(img, startX, startY, targetW, targetH, 0, 0, targetW, targetH);
      
      if (is360) {
        const finalRatio = targetW / targetH;
        console.log(`360° image cropped - Final ratio: ${finalRatio.toFixed(4)} (target: 2.0000), dimensions: ${targetW}x${targetH}`);
      }
      
      resolve(canvas.toDataURL('image/jpeg', 0.95));
    };
    img.onerror = reject;
    img.src = base64Result;
  });
};

// Helper to get input URL from images table
const getInputUrlForJob = async (jobId: string): Promise<string | undefined> => {
  try {
    const { data } = await supabase
      .from('images')
      .select('input_url')
      .eq('id', jobId)
      .single();
    return data?.input_url || undefined;
  } catch {
    return undefined;
  }
};

const is360EquirectangularImage = (width: number, height: number): boolean => {
  const ratio = width / height;
  // 360 equirectangular images are 2:1 aspect ratio (tolerance of ±5%)
  return Math.abs(ratio - 2.0) < 0.1;
};

const prepareImageForAPI = async (file: File): Promise<{
    imageData: string;
    aspectRatio: string;
    restoreRect: { x: number, y: number, width: number, height: number } | null;
    is360: boolean;
}> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const originalW = img.width;
      const originalH = img.height;
      const ratio = originalW / originalH;
      
      // Check if this is a 360 equirectangular image
      const is360 = is360EquirectangularImage(originalW, originalH);
      
      if (is360) {
        // For 360 images, we need to work around Gemini's limitation
        // Use 21:9 (closest supported ratio at 2.33:1) with padding, then crop back to 2:1
        const targetRatio = 21/9; // 2.33:1 - closest to 2:1
        
        // Add minimal vertical padding to reach 21:9
        const paddedH = originalW / targetRatio;
        const offsetY = (paddedH - originalH) / 2;
        
        const canvas = document.createElement('canvas');
        canvas.width = originalW;
        canvas.height = paddedH;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            reject(new Error("Canvas context failed"));
            return;
        }
        
        // Black padding top and bottom
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, originalW, paddedH);
        ctx.drawImage(img, 0, offsetY, originalW, originalH);
        
        // Store info to crop back to original 2:1 ratio
        const restoreRect = {
          x: 0,
          y: offsetY / paddedH,
          width: 1,
          height: originalH / paddedH
        };
        
        resolve({
          imageData: canvas.toDataURL('image/jpeg', 0.95),
          aspectRatio: "21:9", // Use supported ratio
          restoreRect, // Crop back to 2:1 after processing
          is360: true
        });
        return;
      }
      
      // Non-360 images: apply standard padding logic
      const supported = [
        { id: "1:1", val: 1 },
        { id: "3:4", val: 3/4 },
        { id: "4:3", val: 4/3 },
        { id: "9:16", val: 9/16 },
        { id: "16:9", val: 16/9 },
        { id: "21:9", val: 21/9 }
      ];

      const best = supported.reduce((prev, curr) => 
        Math.abs(curr.val - ratio) < Math.abs(prev.val - ratio) ? curr : prev
      );

      let paddedW, paddedH, offsetX, offsetY;
      
      if (ratio > best.val) {
        paddedW = originalW;
        paddedH = originalW / best.val;
        offsetX = 0;
        offsetY = (paddedH - originalH) / 2;
      } else {
        paddedH = originalH;
        paddedW = originalH * best.val;
        offsetX = (paddedW - originalW) / 2;
        offsetY = 0;
      }

      const canvas = document.createElement('canvas');
      canvas.width = paddedW;
      canvas.height = paddedH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
          reject(new Error("Canvas context failed"));
          return;
      }

      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, paddedW, paddedH);
      ctx.drawImage(img, offsetX, offsetY, originalW, originalH);

      const restoreRect = {
        x: offsetX / paddedW,
        y: offsetY / paddedH,
        width: originalW / paddedW,
        height: originalH / paddedH
      };

      resolve({
        imageData: canvas.toDataURL('image/jpeg', 0.95),
        aspectRatio: best.id,
        restoreRect,
        is360: false
      });
    };
    img.onerror = (e) => reject(e);
    img.src = URL.createObjectURL(file);
  });
};

// ============================================================================
// VECTOR/CAD MODE - Real AI integration
// ============================================================================

/**
 * Converts a File to base64 data URL and gets its dimensions
 */
const fileToBase64WithDimensions = (file: File): Promise<{ base64: string; width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      const img = new Image();
      img.onload = () => {
        resolve({ base64, width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = base64;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Generates vector floor plan data from an image using AI
 */
const generateVectorPlan = async (file: File): Promise<VectorFloorPlan> => {
  console.log('generateVectorPlan called with file:', file.name);
  
  // Convert file to base64 and get dimensions
  const { base64, width, height } = await fileToBase64WithDimensions(file);
  
  // Call the AI extraction function
  const result = await extractFloorPlanVectors(base64, width, height);
  
  // Convert to VectorFloorPlan format (adding thickness to walls)
  const vectorPlan: VectorFloorPlan = {
    width: result.width,
    height: result.height,
    walls: result.walls.map(wall => ({
      id: wall.id,
      x1: wall.x1,
      y1: wall.y1,
      x2: wall.x2,
      y2: wall.y2,
      thickness: wall.thickness,
    })),
    labels: result.labels.map(label => ({
      id: label.id,
      x: label.x,
      y: label.y,
      text: label.text,
      fontSize: label.fontSize,
    })),
  };
  
  console.log('Vector plan generated:', vectorPlan);
  return vectorPlan;
};


function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPricingBenefitsModal, setShowPricingBenefitsModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [showDashboard, setShowDashboard] = useState(false);
  const [dashboardInitialTab, setDashboardInitialTab] = useState<'subscription' | 'topup' | undefined>(undefined);
  const [dashboardInitialCreditsAmount, setDashboardInitialCreditsAmount] = useState<number | undefined>(undefined);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState<'admin' | 'superadmin' | 'editor' | null>(null);
  const [appState, setAppState] = useState<AppState>(AppState.INITIALIZING);
  const [images, setImages] = useState<ImageState>({
    originalFile: null,
    originalUrl: null,
    processedUrl: null,
    maskDataUrl: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [isUploadingOriginal, setIsUploadingOriginal] = useState(false);
  const [isImagePaid, setIsImagePaid] = useState(false);
  const [isConsumingCredit, setIsConsumingCredit] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  
  // Two-path workflow state - Vector mode is now the default (accuracy-first)
  const [selectedMode, setSelectedMode] = useState<GenerationMode>('vector');
  const [vectorData, setVectorData] = useState<VectorFloorPlan | null>(null);
  
  // Pro Order Modal state - supports multiple sketches
  const [showProOrderModal, setShowProOrderModal] = useState(false);
  // Result banner after returning from a card payment ('success' | 'canceled').
  const [proOrderPaymentResult, setProOrderPaymentResult] = useState<'success' | 'canceled' | null>(null);
  const [showGuestOrder, setShowGuestOrder] = useState(() => parseSketchplanEntry().openOrder);
  const [resumeGuestOrder, setResumeGuestOrder] = useState(false);
  const [orderPropertyType, setOrderPropertyType] = useState<SketchplanPropertyType | null>(
    () => parseSketchplanEntry().propertyType,
  );
  const [orderCurrency, setOrderCurrency] = useState<SketchplanCurrency | null>(
    () => parseSketchplanEntry().currency,
  );
  const [guestConfirmedEmail, setGuestConfirmedEmail] = useState<string | null>(null);
  const [guestConfirmedRef, setGuestConfirmedRef] = useState<string | null>(null);
  const [loginPrefillEmail, setLoginPrefillEmail] = useState('');
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [showReferrals, setShowReferrals] = useState(false);
  const [proOrderSketchUrls, setProOrderSketchUrls] = useState<string[]>([]);
  const [proOrderSketchStorageUrls, setProOrderSketchStorageUrls] = useState<string[]>([]);
  const [proOrderSketchFiles, setProOrderSketchFiles] = useState<File[]>([]);
  const [isUploadingProSketch, setIsUploadingProSketch] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Detect return from a card-payment Stripe Checkout and show a confirmation.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('pro_order') || params.get('guest_order');
    const ref = params.get('ref');
    const claim = params.get('claim');
    const emailParam = params.get('email');
    if (emailParam) setLoginPrefillEmail(emailParam);
    if (claim === '1') {
      setAuthMode(emailParam ? 'signup' : 'login');
      setShowLoginModal(true);
    }
    if (result === 'success' || result === 'canceled') {
      if (params.get('guest_order')) {
        const draft = loadDraft();
        if (result === 'success') {
          trackSketchplan('sketchplan_guest_payment_completed', { status: 'success' });
          setGuestConfirmedEmail(draft?.email || emailParam || '');
          setGuestConfirmedRef(ref);
        } else {
          setResumeGuestOrder(true);
          setShowGuestOrder(true);
        }
      } else {
        setProOrderPaymentResult(result);
      }
      params.delete('pro_order');
      params.delete('guest_order');
      params.delete('ref');
      params.delete('claim');
      params.delete('email');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  // Branding profile – persisted to localStorage
  const [brandingProfile, setBrandingProfile] = useState<BrandingProfile>(() => {
    try {
      const stored = localStorage.getItem('sketchplan_branding_profile');
      if (stored) return { ...DEFAULT_BRANDING_PROFILE, ...JSON.parse(stored) };
    } catch { /* ignore parse errors */ }
    return DEFAULT_BRANDING_PROFILE;
  });
  const [showBrandingModal, setShowBrandingModal] = useState(false);

  const handleBrandingChange = useCallback((profile: BrandingProfile) => {
    setBrandingProfile(profile);
    try { localStorage.setItem('sketchplan_branding_profile', JSON.stringify(profile)); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    const onPopState = () => {
      setCurrentPath(window.location.pathname);
      const entry = parseSketchplanEntry();
      setShowGuestOrder(entry.openOrder);
      setOrderPropertyType(entry.propertyType);
      setOrderCurrency(entry.currency);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigateOrder = useCallback((
    type?: SketchplanPropertyType | null,
    currency?: SketchplanCurrency | null,
  ) => {
    const entry = parseSketchplanEntry();
    const nextType = type ?? entry.propertyType;
    const nextCurrency = currency ?? entry.currency;
    const next = orderPath(nextType, nextCurrency);
    window.history.pushState({}, '', next);
    setCurrentPath(window.location.pathname);
    setOrderPropertyType(nextType);
    setOrderCurrency(nextCurrency);
    setResumeGuestOrder(false);
    setShowGuestOrder(true);
  }, []);

  const closeOrder = useCallback(() => {
    setShowGuestOrder(false);
    setResumeGuestOrder(false);
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/order') {
      window.history.pushState({}, '', '/');
      setCurrentPath('/');
    }
  }, []);

  useEffect(() => {
    const initApp = async () => {
        // AI processing now runs through the `process-image` Supabase Edge
        // Function (auth'd via the user's Supabase session), so the legacy
        // Gemini-key gate is no longer needed — go straight to UPLOAD.
        setAppState(AppState.UPLOAD);

        if (!isConfigured) {
          setAuthLoading(false);
          return;
        }

        if (window.location.pathname === '/dashboard') {
            setShowDashboard(true);
        }

        const isAuthCallback = window.location.pathname === '/auth/callback';
        
        // Check for auth tokens in URL hash (mobile deep link handling)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);
        const hasAuthTokens = hashParams.get('access_token') || queryParams.get('code');
        
        // Check for SSO tokens from cross-app navigation
        const ssoAccessToken = hashParams.get('sso_access_token');
        const ssoRefreshToken = hashParams.get('sso_refresh_token');
        
        if (ssoAccessToken && ssoRefreshToken) {
          console.log('SSO tokens detected, setting session...');
          // Clear the hash immediately for security
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Set the session using the SSO tokens
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: ssoAccessToken,
              refresh_token: ssoRefreshToken
            });
            
            if (data.session) {
              console.log('SSO session established successfully');
              setSession(data.session);
              fetchProfile(data.session.user.id);
              setAuthLoading(false);
              return; // Exit early, session is set
            } else if (error) {
              console.warn('SSO session failed:', error.message);
            }
          } catch (err) {
            console.warn('SSO session error:', err);
          }
        }

        // Cross-device magic links. The PKCE `?code=` exchange (handled by
        // detectSessionInUrl) only works in the SAME browser that requested
        // the link, because the code-verifier lives in that browser's storage.
        // External clients routinely request a link on desktop and open it on
        // their phone (or inside an email app's in-app browser), so the code
        // exchange fails and /auth/callback just times out — they can never
        // sign in to check their order. Supabase's `token_hash` flow verifies
        // without a local verifier, so handle it explicitly here. (Requires the
        // magic-link email template to use {{ .TokenHash }} → see PR notes.)
        const otpTokenHash = queryParams.get('token_hash');
        if (otpTokenHash) {
          const otpTypes = ['email', 'magiclink', 'signup', 'invite', 'recovery', 'email_change'] as const;
          const rawOtpType = queryParams.get('type') || 'email';
          const otpType = (otpTypes as readonly string[]).includes(rawOtpType)
            ? (rawOtpType as (typeof otpTypes)[number])
            : 'email';
          try {
            const { data, error } = await supabase.auth.verifyOtp({ token_hash: otpTokenHash, type: otpType });
            if (!error && data.session) {
              window.history.replaceState({}, document.title, '/');
              setCurrentPath('/');
              setSession(data.session);
              fetchProfile(data.session.user.id, true);
              setAuthLoading(false);
              return;
            }
            if (error) console.warn('verifyOtp (token_hash) failed:', error.message);
          } catch (err) {
            console.warn('verifyOtp (token_hash) error:', err);
          }
        }

        // Implicit-flow hash tokens also work cross-device. detectSessionInUrl
        // usually consumes these, but handle them defensively so a magic link
        // that lands with #access_token still completes sign-in.
        const implicitAccessToken = hashParams.get('access_token');
        const implicitRefreshToken = hashParams.get('refresh_token');
        if (implicitAccessToken && implicitRefreshToken) {
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: implicitAccessToken,
              refresh_token: implicitRefreshToken,
            });
            if (!error && data.session) {
              window.history.replaceState({}, document.title, '/');
              setCurrentPath('/');
              setSession(data.session);
              fetchProfile(data.session.user.id, true);
              setAuthLoading(false);
              return;
            }
            if (error) console.warn('setSession (hash tokens) failed:', error.message);
          } catch (err) {
            console.warn('setSession (hash tokens) error:', err);
          }
        }

        // Timeout fallback for auth callback - if code exchange takes too long, redirect to home
        if (isAuthCallback) {
          setTimeout(() => {
            // If still on callback page after 8s and no session, redirect to home
            if (window.location.pathname === '/auth/callback') {
              console.warn('Auth callback timeout - redirecting to home');
              window.history.replaceState({}, document.title, "/");
              setCurrentPath("/");
              setAuthLoading(false);
            }
          }, 8000);
        }

        // Always fetch session, including on auth callback
        // This ensures the session is ready before rendering
        supabase.auth.getSession().then(({ data: { session } }) => {
          setSession(session);
          if (session) {
              // If this is auth callback, redirect and show dashboard after profile loads
              if (isAuthCallback) {
                  window.history.replaceState({}, document.title, "/");
                  setCurrentPath("/");
                  fetchProfile(session.user.id, true); // Show dashboard after profile loads
              } else {
                  fetchProfile(session.user.id);
              }
          } else if (!isAuthCallback) {
              // Only set loading false if NOT on auth callback
              // Auth callback will be handled by onAuthStateChange after code exchange
              setAuthLoading(false);
          }
          // If isAuthCallback and no session yet, keep authLoading true
          // onAuthStateChange will fire once Supabase exchanges the code
        }).catch(err => {
          console.warn("Supabase auth session check failed:", err);
          if (!isAuthCallback) {
            setAuthLoading(false);
          }
        });
    };

    initApp();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);

      if (!session) {
        setUserProfile(null);
        setAuthLoading(false);
        setShowDashboard(false);
        return;
      }

      // Defer all Supabase client calls — awaiting rpc/from inside onAuthStateChange
      // deadlocks the auth lock and makes login hang forever.
      setTimeout(() => {
        void (async () => {
          try {
            const { data: claimed } = await supabase.rpc('claim_guest_sketchplan_orders');
            if (claimed?.claimed > 0) {
              trackSketchplan('sketchplan_guest_order_claimed', { claimed: claimed.claimed });
              setShowDashboard(true);
            }
          } catch {
            /* claim is best-effort */
          }

          const pendingConsent = localStorage.getItem('pending_marketing_consent');
          if (pendingConsent === 'true') {
            localStorage.removeItem('pending_marketing_consent');
            await supabase
              .from('profiles')
              .update({
                marketing_consent: true,
                marketing_consent_at: new Date().toISOString(),
              })
              .eq('id', session.user.id);
          } else if (pendingConsent === 'false') {
            localStorage.removeItem('pending_marketing_consent');
          }

          const pendingRef = localStorage.getItem('referral_code');
          if (pendingRef) {
            localStorage.removeItem('referral_code');
            const provider = session.user?.app_metadata?.provider;
            const authMethod =
              provider === 'google' ? 'google' : provider === 'email' ? 'magic_link' : null;
            void supabase
              .rpc('apply_referral_code', {
                p_code: pendingRef,
                p_auth_method: authMethod,
                p_app_source: 'sketchplan',
              })
              .catch(() => {});
          }
        })();
      }, 0);

      setShowLoginModal(false);

      if (pendingFile) {
        fetchProfile(session.user.id);
        const file = pendingFile;
        setPendingFile(null);
        setShowLoginPrompt(false);
        const url = URL.createObjectURL(file);
        setImages({
          originalFile: file,
          originalUrl: url,
          processedUrl: null,
          maskDataUrl: null,
        });
        setProOrderSketchUrls([url]);
        setProOrderSketchFiles([file]);
        setShowProOrderModal(true);
        return;
      }

      const path = window.location.pathname;

      if (path === '/auth/callback' || path === '/login') {
        window.history.replaceState({}, document.title, '/');
        setCurrentPath('/');
        fetchProfile(session.user.id, true);
      } else if (path === '/dashboard') {
        fetchProfile(session.user.id, true);
      } else {
        fetchProfile(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user) return;

    const channel = supabase
      .channel('profile-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${session.user.id}`,
        },
        (payload) => {
          if (payload.new) {
              setUserProfile(payload.new as UserProfile);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session]);

  const fetchProfile = async (userId: string, showDashboardAfter: boolean = false) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      setUserProfile(data);
      
      // Check admin status
      const role = await getAdminRole(userId);
      setAdminRole(role);
      setIsAdmin(role === 'admin' || role === 'superadmin' || role === 'editor');
    } catch (error) {
      console.error('Error fetching profile:', error);
      const currentUser = (await supabase.auth.getUser()).data.user;
      if (currentUser) {
          setUserProfile({ id: userId, email: currentUser.email || '', credits: 10 });
      }
    } finally {
      setAuthLoading(false);
      // Show dashboard after profile is loaded (for auth callbacks)
      if (showDashboardAfter) {
        setShowDashboard(true);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      window.location.reload(); 
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleOpenDashboard = () => {
      if (session && userProfile) {
          setShowDashboard(true);
      } else {
          setAuthMode('login');
          setShowLoginModal(true);
      }
  };

  const handleImagesSelected = async (files: File[]) => {
    // Check if user is logged in
    if (!session) {
      // Store first file for pending login flow
      setPendingFile(files[0]);
      setShowLoginPrompt(true);
      return;
    }
    
    // Upload sketches → Show order form (customer enters sq ft and floors manually)
    setShowDashboard(false);
    
    // Create preview URLs for all files
    const urls = files.map(file => URL.createObjectURL(file));
    
    // Store first file as primary for legacy compatibility
    setImages({
      ...images,
      originalFile: files[0],
      originalUrl: urls[0],
      processedUrl: null
    });
    
    // Store all files and URLs
    setProOrderSketchFiles(files);
    setProOrderSketchUrls(urls);
    setProOrderSketchStorageUrls([]);
    
    // Show the order modal and start upload
    setShowProOrderModal(true);
    setIsUploadingProSketch(true);
    setUploadProgress(0);

    // Upload all sketches to storage for admin access and order records
    try {
      if (session?.user) {
        const uploadedUrls: string[] = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const uploadedUrl = await uploadImageToStorage(session.user.id, file, 'sketch');
          if (uploadedUrl) {
            uploadedUrls.push(uploadedUrl);
          }
          setUploadProgress(Math.round(((i + 1) / files.length) * 100));
        }
        setProOrderSketchStorageUrls(uploadedUrls);
      }
    } catch (err) {
      console.error('Failed to upload sketches for pro order:', err);
    } finally {
      setIsUploadingProSketch(false);
    }
  };
  
  // Legacy single-file handler for dashboard/gallery
  const handleImageSelected = async (file: File) => {
    await handleImagesSelected([file]);
  };
  
  // Direct vector processing for uploaded files (vector-first flow)
  const handleVectorProcessDirect = async (file: File) => {
    if (!userProfile) {
      setError('Please log in to process floor plans.');
      setAppState(AppState.UPLOAD);
      return;
    }
    
    const creditCost = 5; // Vector mode costs 5 credits
    
    // Consume credits
    if (isConsumingCredit) return;
    setIsConsumingCredit(true);
    
    let consumedCount = 0;
    for (let i = 0; i < creditCost; i++) {
      const result = await consumeCredit();
      if (result !== 'success') {
        for (let j = 0; j < consumedCount; j++) {
          await refundCredit(session?.user?.id || '');
        }
        setUserProfile(prev => prev ? { ...prev, credits: prev.credits + consumedCount } : null);
        setIsConsumingCredit(false);
        if (result === 'insufficient_credits') {
          setShowBuyCreditsModal(true);
        }
        setAppState(AppState.UPLOAD);
        return;
      }
      consumedCount++;
    }
    
    setError(null);
    
    try {
      // Call the vector plan generator
      const vectorResult = await generateVectorPlan(file);
      
      // Validate the result has actual data
      console.log('Vector extraction result:', {
        wallCount: vectorResult.walls?.length || 0,
        labelCount: vectorResult.labels?.length || 0,
        width: vectorResult.width,
        height: vectorResult.height
      });
      
      if (!vectorResult.walls || vectorResult.walls.length === 0) {
        throw new Error('No walls detected in the floor plan. Please ensure the sketch has clear wall lines.');
      }
      
      setVectorData(vectorResult);
      setSelectedMode('vector');
      
      setAppState(AppState.RESULT);
      setIsConsumingCredit(false);
    } catch (err: any) {
      console.error('Vector processing failed:', err);
      
      // Refund credits
      if (session?.user) {
        for (let i = 0; i < creditCost; i++) {
          await refundCredit(session.user.id);
        }
        setUserProfile(prev => prev ? { ...prev, credits: prev.credits + creditCost } : null);
      }
      
      setIsConsumingCredit(false);
      const errorMessage = err?.message || 'Floor plan conversion failed';
      setError(`${errorMessage}. Your ${creditCost} credits have been refunded.`);
      setAppState(AppState.UPLOAD);
    }
  };

  // Handle proceeding after login with pending file
  const handleLoginPromptAuth = (mode: 'login' | 'signup') => {
    setShowLoginPrompt(false);
    setAuthMode(mode);
    setShowLoginModal(true);
  };

  // Handle mode selection (Visual Staging vs Editable CAD)
  const handleModeSelected = async (mode: GenerationMode) => {
    setSelectedMode(mode);
    
    if (mode === 'visual') {
      // Visual mode: go to Editor for style selection and processing
      setAppState(AppState.EDITOR);
    } else {
      // Vector/CAD mode: process immediately with vector generation
      await handleVectorProcess();
    }
  };

  // Handle vector/CAD processing
  const handleVectorProcess = async () => {
    if (!images.originalFile || !userProfile) return;
    
    const creditCost = 5; // Vector mode costs 5 credits
    
    // Consume credits
    if (isConsumingCredit) return;
    setIsConsumingCredit(true);
    
    let consumedCount = 0;
    for (let i = 0; i < creditCost; i++) {
      const result = await consumeCredit();
      if (result !== 'success') {
        for (let j = 0; j < consumedCount; j++) {
          await refundCredit(session?.user?.id || '');
        }
        setUserProfile(prev => prev ? { ...prev, credits: prev.credits + consumedCount } : null);
        setIsConsumingCredit(false);
        if (result === 'insufficient_credits') {
          setShowBuyCreditsModal(true);
        }
        return;
      }
      consumedCount++;
    }
    
    setAppState(AppState.PROCESSING);
    setError(null);
    
    try {
      // Call the vector plan generator (placeholder)
      const vectorResult = await generateVectorPlan(images.originalFile);
      setVectorData(vectorResult);
      
      setAppState(AppState.RESULT);
      setIsConsumingCredit(false);
    } catch (err: any) {
      console.error('Vector processing failed:', err);
      
      // Refund credits
      if (session?.user) {
        for (let i = 0; i < creditCost; i++) {
          await refundCredit(session.user.id);
        }
        setUserProfile(prev => prev ? { ...prev, credits: prev.credits + creditCost } : null);
      }
      
      setIsConsumingCredit(false);
      setError(`CAD generation failed. Your ${creditCost} credits have been refunded.`);
      setAppState(AppState.MODE_SELECTION);
    }
  };

  const consumeCredit = async (): Promise<'success' | 'insufficient_credits' | 'error'> => {
    if (!session?.user) return 'error';
    try {
        const { data, error } = await supabase.rpc('consume_credit', { user_id: session.user.id });
        
        if (error) {
            console.error("Credit consumption error:", error);
            if (error.message.includes('INSUFFICIENT_CREDITS')) return 'insufficient_credits';
            return 'error';
        }
        
        // Optimistically update UI
        if (userProfile) {
            setUserProfile(prev => prev ? { ...prev, credits: prev.credits - 1 } : null);
        }
        
        return 'success';
    } catch (err) {
        console.error("Credit RPC failed:", err);
        return 'error';
    }
  };

  // --- CONTINUE EDITING (NO CREDIT CHARGE) ---
  // Opens the AI Editor (ResultViewer) with the already-processed image
  const handleContinueEditing = (file: File) => {
    setShowDashboard(false);
    const url = URL.createObjectURL(file);
    // Set both original and processed to the same URL since it's already processed
    setImages({
        originalFile: file,
        originalUrl: url,
        processedUrl: url, // Already processed - use same image
        maskDataUrl: null
    });
    setSelectedMode('visual'); // Use visual mode for AI editing
    setAppState(AppState.RESULT); // Go directly to ResultViewer with AI Editor
    setIsImagePaid(true); // Mark as paid so no credit is charged
  };

  // --- AUTO PROCESS ---
  const handleAutoProcess = async (file: File) => {
    if (!userProfile) return;
    
    // Prevent double-click race condition
    if (isConsumingCredit) {
        console.log('Blocked duplicate auto-process call');
        return;
    }
    setIsConsumingCredit(true);
    
    // 1. DEDUCT CREDIT FIRST
    const creditResult = await consumeCredit();
    if (creditResult !== 'success') {
        setIsConsumingCredit(false);
        if (creditResult === 'insufficient_credits') {
            setShowBuyCreditsModal(true);
        } else {
            alert("Transaction failed. Please try again.");
        }
        return; 
    }

    setShowDashboard(false);
    const url = URL.createObjectURL(file);
    setImages({
        originalFile: file,
        originalUrl: url,
        processedUrl: null,
        maskDataUrl: null
    });
    setAppState(AppState.PROCESSING);
    setIsImagePaid(true);
    
    // 2. Generate Job ID for Gallery Pairing
    const jobId = crypto.randomUUID();
    
    // 3. Create generation record for tracking
    const generationId = await createGenerationRecord(
        session?.user?.id,
        userProfile?.email,
        { action: 'auto', job_id: jobId }
    );

    // 4. TRY BLOCK FOR PROCESS & UPLOAD
    let inputUrl: string | undefined;
    try {
        // Upload Original
        if (session?.user) {
             // IMPORTANT: Insert record BEFORE upload so trigger doesn't create with wrong service_type
             console.log('SKETCHPLAN: Pre-inserting with service_type: sketchplan', { jobId });
             await supabase.from('images').upsert({
                    id: jobId,
                    user_id: session.user.id,
                    input_url: '',
                    output_url: '',
                    status: 'processing',
                    type: 'restoration',
                    service_type: 'sketchplan'
             });
             
             inputUrl = await uploadImageToStorage(session.user.id, file, 'original', jobId);
             if (inputUrl) {
                // Update with actual input URL after upload
                await supabase.from('images').update({
                    input_url: inputUrl
                }).eq('id', jobId);
             }
        }

        const { imageData, aspectRatio, restoreRect, is360 } = await prepareImageForAPI(file);
        
        // Show warning for 360 images
        if (is360) {
          console.log('Processing 360 equirectangular image using 21:9 workaround, will crop back to 2:1');
        }
        
        // Client-side Gemini processing
        const resultBase64 = await declutterImage(imageData, AUTO_DECLUTTER_PROMPT, aspectRatio);
        const croppedResult = await cropResultToOriginal(resultBase64, restoreRect, is360);

        // Upload Result and Update DB
        let outputUrl: string | undefined;
        if (session?.user) {
             outputUrl = await uploadImageToStorage(session.user.id, croppedResult, 'processed', jobId);
             if (outputUrl) {
                await supabase.from('images').update({
                    output_url: outputUrl,
                    status: 'completed',
                    type: 'restoration'
                }).eq('id', jobId);
             }
        }

        if (!croppedResult) {
            throw new Error('Processing returned empty result');
        }
        
        // Update generation record to completed with input and output URLs
        if (generationId) {
            await updateGenerationRecord(generationId, 'completed', 1, undefined, outputUrl, inputUrl);
        }
        
        // Processing complete - go back to Dashboard
        setIsConsumingCredit(false);
        setAppState(AppState.UPLOAD);
        setTimeout(() => setShowDashboard(true), 200);
    } catch (err: any) {
        console.error("Auto Process failed:", err);
        
        // Update generation record to failed
        if (generationId) {
            await updateGenerationRecord(generationId, 'failed', 0, err.message);
        }
        
        // Refund credit
        if (session?.user) {
            await refundCredit(session.user.id);
            setUserProfile(prev => prev ? { ...prev, credits: prev.credits + 1 } : null);
            await supabase.from('images').update({ status: 'failed' }).eq('id', jobId);
        }
        
        setIsImagePaid(false);
        setError("Processing failed. Your credit has been refunded. Please try again.");
        setAppState(AppState.EDITOR);
        setIsConsumingCredit(false);
    }
  };

  // --- MANUAL PROCESS ---
  const handleProcess = async (
    maskedImageData: string, 
    prompt: string, 
    aspectRatio: string, 
    restoreRect: {x: number, y: number, width: number, height: number} | null,
    is360?: boolean,
    creditCost: number = 10,
    referenceImageData?: string
  ) => {
    if (!isImagePaid) {
        if (isConsumingCredit) return;
        
        const confirm = window.confirm(`Convert this sketch? ${creditCost} Credits will be deducted.`);
        if (!confirm) return;

        setIsConsumingCredit(true);
        
        // Consume multiple credits for floor plan conversion
        let consumedCount = 0;
        for (let i = 0; i < creditCost; i++) {
            const result = await consumeCredit();
            if (result !== 'success') {
                // Refund any already consumed credits
                for (let j = 0; j < consumedCount; j++) {
                    await refundCredit(session?.user?.id || '');
                }
                setUserProfile(prev => prev ? { ...prev, credits: prev.credits + consumedCount } : null);
                setIsConsumingCredit(false);
                if (result === 'insufficient_credits') setShowBuyCreditsModal(true);
                return;
            }
            consumedCount++;
        }
        
        setIsImagePaid(true);
        setIsConsumingCredit(false);
    }

    setAppState(AppState.PROCESSING);
    setError(null);

    const jobId = crypto.randomUUID();
    
    // Create generation record
    const generationId = await createGenerationRecord(
        session?.user?.id,
        userProfile?.email,
        { action: 'manual', job_id: jobId, prompt: prompt?.substring(0, 100) }
    );

    let inputUrl: string | undefined;
    try {
      if (session?.user && images.originalFile) {
          // IMPORTANT: Insert record BEFORE upload so trigger doesn't create with wrong service_type
          console.log('SKETCHPLAN: Pre-inserting with service_type: sketchplan', { jobId });
          await supabase.from('images').upsert({
                  id: jobId,
                  user_id: session.user.id,
                  input_url: '',
                  output_url: '',
                  status: 'processing',
                  type: 'restoration',
                  service_type: 'sketchplan'
          });
          
          inputUrl = await uploadImageToStorage(session.user.id, images.originalFile, 'original', jobId);
          if (inputUrl) {
              // Update with actual input URL after upload
              await supabase.from('images').update({
                  input_url: inputUrl
              }).eq('id', jobId);
          }
      }

      // Show warning for 360 images
      if (is360) {
        console.warn('Processing 360 equirectangular image - aspect ratio preserved');
      }
      
      // Client-side Gemini processing
      // Use reference image for style consistency if available
      let resultBase64: string;
      if (referenceImageData) {
        console.log('Using reference image for floor plan conversion');
        resultBase64 = await convertFloorPlanWithReference(maskedImageData, referenceImageData, prompt, aspectRatio);
      } else {
        resultBase64 = await declutterImage(maskedImageData, prompt, aspectRatio);
      }
      const croppedResult = await cropResultToOriginal(resultBase64, restoreRect, is360);

      let outputUrl: string | undefined;
      if (session?.user) {
           outputUrl = await uploadImageToStorage(session.user.id, croppedResult, 'processed', jobId);
           if (outputUrl) {
                await supabase.from('images').update({
                    output_url: outputUrl,
                    status: 'completed',
                    type: 'restoration'
                }).eq('id', jobId);
           }
      }

      if (!croppedResult) {
          throw new Error('Processing returned empty result');
      }
      
      // Update generation to completed with input and output URLs
      if (generationId) {
          await updateGenerationRecord(generationId, 'completed', creditCost, undefined, outputUrl, inputUrl);
      }
      
      setImages(prev => ({
          ...prev,
          maskDataUrl: maskedImageData,
          processedUrl: croppedResult
      }));
      
      setAppState(AppState.RESULT);
      setIsConsumingCredit(false);
    } catch (err: any) {
      console.error("Process failed:", err);
      
      // Update generation record to failed
      if (generationId) {
          await updateGenerationRecord(generationId, 'failed', 0, err?.message);
      }
      
      const errorMsg = err?.message || String(err) || '';

      // Refund all credits and update DB status
      if (session?.user) {
          for (let i = 0; i < creditCost; i++) {
              await refundCredit(session.user.id);
          }
          setUserProfile(prev => prev ? { ...prev, credits: prev.credits + creditCost } : null);
          await supabase.from('images').update({ status: 'failed' }).eq('id', jobId);
      }
      
      setIsImagePaid(false);
      setIsConsumingCredit(false);

      setError(`Processing failed. Your ${creditCost} credits have been refunded. Please try again.`);
      setAppState(AppState.EDITOR);
    }
  };

  const handleUpscale = async () => {
    if (!images.processedUrl) return;
    
    setIsUpscaling(true);
    try {
      const upscaledBase64 = await upscaleImage(images.processedUrl);
      
      setImages(prev => ({
        ...prev,
        processedUrl: upscaledBase64
      }));
    } catch (err: any) {
      console.error(err);
      alert("Upscaling failed. Please try again.");
    } finally {
      setIsUpscaling(false);
    }
  };

  // Handle credit consumption for downloads
  const handleConsumeCreditsForDownload = useCallback(async (amount: number): Promise<boolean> => {
    if (!session?.user || !userProfile) return false;
    
    if (userProfile.credits < amount) {
      return false;
    }
    
    try {
      // Consume credits one by one
      for (let i = 0; i < amount; i++) {
        const result = await consumeCredit();
        if (result !== 'success') {
          // Refund any already consumed
          for (let j = 0; j < i; j++) {
            await refundCredit(session.user.id);
          }
          setUserProfile(prev => prev ? { ...prev, credits: prev.credits + i } : null);
          return false;
        }
      }
      return true;
    } catch (err) {
      console.error('Error consuming credits:', err);
      return false;
    }
  }, [session, userProfile, consumeCredit]);

  // Handle Order Pro Floor Plan
  const handleOrderPro = useCallback((sketchUrl: string) => {
    setProOrderSketchUrls([sketchUrl]);
    setShowProOrderModal(true);
  }, []);

  // Submit pro order to database, consume credits, and send email notification
  const handleSubmitProOrder = useCallback(async (orderData: ProOrderData): Promise<boolean> => {
    if (!session?.user || !userProfile) return false;
    
    const creditCost = orderData.creditCost;

    // Monthly-invoice clients are billed on a statement each month — they do not
    // consume credits at order time. Their order records the £ amount owed.
    const isInvoiceClient =
      orderData.paymentMethod === 'invoice' || userProfile.billing_mode === 'monthly_invoice';
    // Card (pay-per-plan) orders pay by Stripe now and never touch credits.
    const isCardOrder = orderData.paymentMethod === 'card';

    // Check credits (prepaid credit clients only)
    if (!isInvoiceClient && !isCardOrder && userProfile.credits < creditCost) {
      console.error('Insufficient credits');
      return false;
    }

    // Build branding notes section for the CAD team (shared by all payment paths)
    const brandingNotes = orderData.branding?.agencyName
      ? `\n\n--- BRANDING SPEC ---\nAgency: ${orderData.branding.agencyName}${orderData.branding.tagline ? `\nTagline: ${orderData.branding.tagline}` : ''}\nPrimary colour: ${orderData.branding.primaryColor}\nAccent colour: ${orderData.branding.accentColor}\nFooter bg: ${orderData.branding.footerBgColor} / text: ${orderData.branding.footerTextColor}\nFont: ${orderData.branding.fontFamily}\nWatermark: ${orderData.branding.watermarkEnabled ? `Yes (${orderData.branding.watermarkPosition}, ${Math.round(orderData.branding.watermarkOpacity * 100)}% opacity)` : 'No'}\nLogo in footer: ${orderData.branding.showLogoInFooter ? 'Yes' : 'No'}`
      : '';
    const sketchNotes = orderData.notes + (orderData.sketchUrls.length > 1 ? ` [${orderData.sketchUrls.length} sketch files attached]` : '') + brandingNotes;

    // Pay-per-plan by card: the server quotes the price with sketchplan_quote(),
    // creates the 'awaiting_payment' order and the Stripe Checkout Session. The
    // webhook flips it to 'pending' (joining the CAD queue) and emails the team
    // once payment clears. No credits are used.
    if (isCardOrder) {
      try {
        const { data: checkout, error: checkoutError } = await supabase.functions.invoke('sketchplan-guest-checkout', {
          body: {
            propertyType: orderData.propertyType,
            estimatedSqFt: orderData.estimatedSqFt,
            floors: orderData.floors,
            turnaround: orderData.turnaround,
            currency: 'gbp',
            propertyAddress: orderData.propertyAddress,
            notes: orderData.notes,
            brandingNotes: brandingNotes ? brandingNotes.replace(/^\s*--- BRANDING SPEC ---\n/, '') : undefined,
            guestName: String(session.user.user_metadata?.full_name || session.user.user_metadata?.name || ''),
            guestEmail: orderData.contactEmail || session.user.email,
            contactPhone: orderData.contactPhone,
            sketchUrls: orderData.sketchUrls,
            successPath: '/',
            cancelPath: '/',
          },
        });

        if (checkoutError || (checkout as any)?.error || !checkout?.url) {
          console.error('Error creating order checkout:', checkoutError || (checkout as any)?.error);
          return false;
        }

        await logEvent('pro_order_card_checkout', session.user.id, session.user.email, null, null, {
          order_id: checkout.orderId,
          amount_pence: checkout.amountPence,
          property_type: orderData.propertyType,
          turnaround: orderData.turnaround,
        });

        window.location.href = checkout.url;
        return true;
      } catch (err) {
        console.error('Error submitting card order:', err);
        return false;
      }
    }
    
    try {
      // Consume credits first (prepaid clients only)
      let consumedCount = 0;
      if (!isInvoiceClient) {
        for (let i = 0; i < creditCost; i++) {
          const result = await consumeCredit();
          if (result !== 'success') {
            // Refund any already consumed
            for (let j = 0; j < consumedCount; j++) {
              await refundCredit(session.user.id);
            }
            setUserProfile(prev => prev ? { ...prev, credits: prev.credits + consumedCount } : null);
            console.error('Credit consumption failed');
            return false;
          }
          consumedCount++;
        }
      }
      
      // Insert order into database
      // Store multiple sketch URLs as JSON array in sketch_url field
      const { data, error } = await supabase
        .from('pro_orders')
        .insert({
          user_id: session.user.id,
          property_address: orderData.propertyAddress,
          estimated_sq_ft: orderData.estimatedSqFt || null,
          property_type: orderData.propertyType,
          floors: orderData.floors,
          turnaround: orderData.turnaround,
          notes: sketchNotes,
          sketch_url: JSON.stringify(orderData.sketchUrls),
          contact_email: orderData.contactEmail,
          contact_phone: orderData.contactPhone || null,
          status: 'pending',
          payment_method: isInvoiceClient ? 'invoice' : 'credits',
          // Invoice clients: store the £ amount (pence) owed for monthly billing.
          invoice_amount_pence: isInvoiceClient ? (orderData.invoiceAmountPence ?? null) : null,
          // quoted_price keeps credit cost for prepaid orders, £ amount for invoice orders.
          quoted_price: isInvoiceClient
            ? Number(((orderData.invoiceAmountPence ?? 0) / 100).toFixed(2))
            : creditCost,
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error submitting pro order:', error);
        // Refund credits on DB error (prepaid clients only)
        if (!isInvoiceClient) {
          for (let i = 0; i < creditCost; i++) {
            await refundCredit(session.user.id);
          }
          setUserProfile(prev => prev ? { ...prev, credits: prev.credits + creditCost } : null);
        }
        return false;
      }
      
      // Send email notification to team
      try {
        // Generate signed URLs for all sketches
        const sketchSignedUrls: string[] = [];
        try {
          for (const sketchUrl of orderData.sketchUrls) {
            const splitToken = '/images/';
            const storagePath = sketchUrl.includes(splitToken)
              ? sketchUrl.split(splitToken)[1]?.split('?')[0] || null
              : null;

            if (storagePath) {
              const { data: signedData } = await supabase.storage
                .from('images')
                .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
              if (signedData?.signedUrl) {
                sketchSignedUrls.push(signedData.signedUrl);
              }
            }
          }
        } catch (err) {
          console.warn('Failed to create signed sketch URLs for email:', err);
        }

        await supabase.functions.invoke('notify-order', {
          body: {
            id: data.id,
            property_address: orderData.propertyAddress,
            estimated_sq_ft: orderData.estimatedSqFt || null,
            property_type: orderData.propertyType,
            floors: orderData.floors,
            turnaround: orderData.turnaround,
            notes: orderData.notes,
            sketch_urls: orderData.sketchUrls,
            sketch_signed_urls: sketchSignedUrls.length > 0 ? sketchSignedUrls : undefined,
            contact_email: orderData.contactEmail,
            contact_phone: orderData.contactPhone || null,
            created_at: data.created_at,
            credit_cost: creditCost,
            room_count: orderData.roomCount,
            complexity: orderData.complexity,
            sketch_count: orderData.sketchUrls.length,
            // Branding spec for the CAD team
            branding: orderData.branding ? {
              agency_name: orderData.branding.agencyName,
              tagline: orderData.branding.tagline,
              primary_color: orderData.branding.primaryColor,
              accent_color: orderData.branding.accentColor,
              footer_bg_color: orderData.branding.footerBgColor,
              footer_text_color: orderData.branding.footerTextColor,
              font_family: orderData.branding.fontFamily,
              watermark_enabled: orderData.branding.watermarkEnabled,
              watermark_position: orderData.branding.watermarkPosition,
              watermark_opacity: orderData.branding.watermarkOpacity,
              show_logo_in_footer: orderData.branding.showLogoInFooter,
              has_logo: !!orderData.branding.logoDataUrl,
            } : null,
          },
        });
        console.log('Order notification email sent');
      } catch (emailErr) {
        // Don't fail the order if email fails
        console.error('Failed to send notification email:', emailErr);
      }
      
      // Log the event
      await logEvent('pro_order_submitted', session.user.id, session.user.email, null, null, {
        property_type: orderData.propertyType,
        estimated_sq_ft: orderData.estimatedSqFt,
        turnaround: orderData.turnaround,
        credit_cost: creditCost,
        room_count: orderData.roomCount,
        complexity: orderData.complexity,
      });
      
      return true;
    } catch (err) {
      console.error('Error submitting pro order:', err);
      return false;
    }
  }, [session, userProfile, consumeCredit]);

  const handleAccept = () => {
    if (images.processedUrl) {
      setImages(prev => ({
        ...prev,
        originalUrl: prev.processedUrl,
        processedUrl: null,
        maskDataUrl: null
      }));
      setAppState(AppState.EDITOR);
      setIsUpscaling(false);
      setIsImagePaid(true); // Keep as paid - no charge for continued edits
    }
  };

  // --- AMENDMENT HANDLER (for floor plan modifications) ---
  const handleAmendment = async (amendmentText: string) => {
    if (!images.processedUrl || !amendmentText.trim()) return;
    
    const creditCost = 10; // Amendment cost
    
    if (isConsumingCredit) return;
    
    setIsConsumingCredit(true);
    
    let consumedCount = 0;
    for (let i = 0; i < creditCost; i++) {
      const result = await consumeCredit();
      if (result !== 'success') {
        for (let j = 0; j < consumedCount; j++) {
          await refundCredit(session?.user?.id || '');
        }
        setUserProfile(prev => prev ? { ...prev, credits: prev.credits + consumedCount } : null);
        setIsConsumingCredit(false);
        if (result === 'insufficient_credits') setShowBuyCreditsModal(true);
        return;
      }
      consumedCount++;
    }
    
    setAppState(AppState.PROCESSING);
    setError(null);
    
    const jobId = crypto.randomUUID();
    const generationId = await createGenerationRecord(
      session?.user?.id,
      userProfile?.email,
      { action: 'amendment', job_id: jobId, prompt: amendmentText.substring(0, 100) }
    );
    
    try {
      // Build amendment prompt
      const amendmentPrompt = `You are editing an existing floor plan image. Apply the following amendments to this floor plan:

AMENDMENTS REQUESTED:
${amendmentText}

IMPORTANT RULES:
- Keep the overall layout and style of the floor plan the same
- Only make the specific changes requested
- Maintain professional quality and consistency
- Do not add elements that weren't requested
- Preserve all existing labels, measurements, and details unless specifically asked to change them`;
      
      // Process the existing floor plan with amendments
      const resultBase64 = await declutterImage(images.processedUrl, amendmentPrompt, '4:3');
      
      // Upload result
      let outputUrl: string | undefined;
      if (session?.user) {
        await supabase.from('images').upsert({
          id: jobId,
          user_id: session.user.id,
          input_url: images.processedUrl,
          output_url: '',
          status: 'processing',
          type: 'amendment',
          service_type: 'sketchplan'
        });
        
        outputUrl = await uploadImageToStorage(session.user.id, resultBase64, 'processed', jobId);
        if (outputUrl) {
          await supabase.from('images').update({
            output_url: outputUrl,
            status: 'completed'
          }).eq('id', jobId);
        }
      }
      
      if (generationId) {
        await updateGenerationRecord(generationId, 'completed', creditCost, undefined, outputUrl);
      }
      
      setImages(prev => ({
        ...prev,
        processedUrl: resultBase64
      }));
      
      setAppState(AppState.RESULT);
      setIsConsumingCredit(false);
    } catch (err: any) {
      console.error('Amendment failed:', err);
      
      if (generationId) {
        await updateGenerationRecord(generationId, 'failed', 0, err?.message);
      }
      
      if (session?.user) {
        for (let i = 0; i < creditCost; i++) {
          await refundCredit(session.user.id);
        }
        setUserProfile(prev => prev ? { ...prev, credits: prev.credits + creditCost } : null);
      }
      
      setIsConsumingCredit(false);
      setError(`Amendment failed. Your ${creditCost} credits have been refunded. Please try again.`);
      setAppState(AppState.RESULT);
    }
  };

  const handleReset = () => {
    if (images.originalUrl) URL.revokeObjectURL(images.originalUrl);
    setImages({
      originalFile: null,
      originalUrl: null,
      processedUrl: null,
      maskDataUrl: null
    });
    setAppState(AppState.UPLOAD);
    setError(null);
    setIsUpscaling(false);
    setIsImagePaid(false);
  };

  const downloadCurrentOriginal = () => {
    if (!images.originalUrl) return;
    const link = document.createElement('a');
    link.href = images.originalUrl;
    link.download = `Sketchplan-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = async (format: 'png' | 'jpeg', watermark?: WatermarkSettings | null) => {
    const processedUrl = images.processedUrl;
    
    if (!processedUrl) {
      console.error('No processed image URL available for download');
      return;
    }
    
    console.log('Starting download:', { format, hasWatermark: !!watermark, urlType: processedUrl.startsWith('data:') ? 'base64' : 'remote' });
    
    // Helper to trigger download from a data URL or blob URL
    const triggerDownload = (url: string, filename: string) => {
      console.log('Triggering download...');
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      // Small delay before removing to ensure download starts
      setTimeout(() => document.body.removeChild(link), 100);
    };
    
    const filename = `Sketchplan-${Date.now()}.${format}`;
    
    // Quick path: If it's base64, no watermark, and format matches - download directly
    if (processedUrl.startsWith('data:') && !watermark) {
      const isJpeg = processedUrl.includes('image/jpeg') || processedUrl.includes('image/jpg');
      const isPng = processedUrl.includes('image/png');
      
      if ((format === 'jpeg' && isJpeg) || (format === 'png' && isPng)) {
        console.log('Direct base64 download (format matches)');
        triggerDownload(processedUrl, filename);
        return;
      }
    }
    
    // For all other cases, use canvas to process/convert the image
    const img = new Image();
    
    // Only set crossOrigin for remote URLs, not for data URLs
    if (!processedUrl.startsWith('data:')) {
      img.crossOrigin = "anonymous";
    }
    
    img.onload = () => {
      console.log('Image loaded for processing:', img.width, 'x', img.height);
      
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.error('Failed to get canvas context');
        // Fallback: try direct download
        triggerDownload(processedUrl, filename);
        return;
      }
      
      ctx.drawImage(img, 0, 0);

      const finishDownload = () => {
        try {
          const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
          const quality = format === 'jpeg' ? 0.95 : undefined;
          const dataUrl = canvas.toDataURL(mimeType, quality);
          console.log('Canvas converted, downloading...');
          triggerDownload(dataUrl, filename);
        } catch (e) {
          console.error('Canvas toDataURL failed:', e);
          // Fallback: try direct download
          triggerDownload(processedUrl, filename);
        }
      };

      if (watermark) {
        const logo = new Image();
        logo.onload = () => {
          const logoWidth = img.width * watermark.scale;
          const logoHeight = logoWidth * (logo.height / logo.width);
          const logoX = img.width * watermark.x;
          const logoY = img.height * watermark.y;
          ctx.globalAlpha = watermark.opacity;
          ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
          ctx.globalAlpha = 1.0;
          finishDownload();
        };
        logo.onerror = () => {
          console.warn('Watermark logo failed to load, downloading without watermark');
          finishDownload();
        };
        logo.src = watermark.imageUrl;
      } else {
        finishDownload();
      }
    };
    
    img.onerror = (e) => {
      console.error('Image failed to load for download:', e);
      
      // Fallback: try fetch + blob approach for remote URLs
      if (!processedUrl.startsWith('data:')) {
        console.log('Trying fetch fallback...');
        fetch(processedUrl)
          .then(response => response.blob())
          .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            triggerDownload(blobUrl, filename);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          })
          .catch(fetchError => {
            console.error('Fetch download also failed:', fetchError);
            // Last resort: open in new tab
            window.open(processedUrl, '_blank');
          });
      } else {
        // For data URLs that somehow failed, try direct download anyway
        triggerDownload(processedUrl, filename);
      }
    };
    
    img.src = processedUrl;
  };

  // Embeddable page routes - render without main app shell
  const isEmbedRoute = currentPath === '/embed' || currentPath === '/pricing' || currentPath === '/signup';
  if (isEmbedRoute) {
     const isIframe = window.self !== window.top;
     return <EmbedPage isEmbed={isIframe} />;
  }

  // PhotoPlan has moved to photoplan.rehub.studio — redirect legacy URLs
  const photoplanViewerMatch = currentPath.match(/^\/photoplan\/([a-zA-Z0-9_-]+)$/);
  if (photoplanViewerMatch) {
    window.location.href = `https://photoplan.rehub.studio/view/${photoplanViewerMatch[1]}`;
    return null;
  }
  if (currentPath === '/photoplan') {
    window.location.href = 'https://photoplan.rehub.studio';
    return null;
  }

  if (authLoading && appState === AppState.INITIALIZING) {
    return (
      <div className="flex items-center justify-center h-screen bg-black flex-col gap-4">
        <Loader2 className="w-10 h-10 text-teal-500 animate-spin" />
        <p className="text-slate-400 animate-pulse">Initializing Application...</p>
      </div>
    );
  }

  if (currentPath === SKETCHPLAN_LOGIN_PATH && !session) {
     return (
       <div className="min-h-screen flex items-center justify-center bg-black p-4">
         <div className="w-full max-w-lg">
           <Login initialMode="login" initialEmail={loginPrefillEmail} />
         </div>
       </div>
     );
  }

  const guestOrderOverlay = (showGuestOrder || currentPath === '/order') ? (
    <GuestOrderFlow
      key={`${orderPropertyType || 'any'}-${orderCurrency || 'detect'}-${resumeGuestOrder ? 'resume' : 'new'}`}
      resume={resumeGuestOrder}
      initialPropertyType={orderPropertyType}
      initialCurrency={orderCurrency}
      customPlanRatePence={userProfile?.custom_plan_rate_pence ?? null}
      onClose={closeOrder}
      onRequestQuote={() => {
        closeOrder();
        window.location.href = 'mailto:hello@sketchplan.com?subject=Sketchplan%20quote%20request';
      }}
    />
  ) : null;

  // Landing page when not authenticated at root or the dedicated order route
  if (!session && (currentPath === '/' || currentPath === '/order')) {
    return (
      <div className="min-h-screen flex flex-col bg-black relative overflow-hidden">
        <FluidBackground />
        <div className="relative z-10 flex flex-col flex-1 min-h-0 overflow-y-auto">
          <Header
            onReset={() => {}}
            showNewPhoto={false}
            user={null}
            onLogin={() => {
              trackSketchplan('sketchplan_client_login_clicked');
              window.history.pushState({}, '', SKETCHPLAN_LOGIN_PATH);
              setCurrentPath(SKETCHPLAN_LOGIN_PATH);
            }}
            onOrder={() => navigateOrder()}
            onOpenDashboard={() => {
              trackSketchplan('sketchplan_client_login_clicked');
              window.history.pushState({}, '', SKETCHPLAN_LOGIN_PATH);
              setCurrentPath(SKETCHPLAN_LOGIN_PATH);
            }}
            isAdmin={isAdmin}
            onOpenAdmin={() => setShowAdminPanel(true)}
          />
          <LandingPage
            onOrder={() => navigateOrder()}
            onLogin={() => {
              trackSketchplan('sketchplan_client_login_clicked');
              window.history.pushState({}, '', SKETCHPLAN_LOGIN_PATH);
              setCurrentPath(SKETCHPLAN_LOGIN_PATH);
            }}
            onOpenPricing={() => setShowPricingBenefitsModal(true)}
          />
          <div className="shrink-0">
            <Footer />
          </div>
          {showPricingBenefitsModal && (
            <PricingBenefitsModal
              onClose={() => setShowPricingBenefitsModal(false)}
              onOrder={() => { setShowPricingBenefitsModal(false); navigateOrder(); }}
            />
          )}
          {showLoginModal && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="w-full max-w-lg">
                <Login
                  onClose={() => setShowLoginModal(false)}
                  initialMode={authMode}
                  initialEmail={loginPrefillEmail}
                />
              </div>
            </div>
          )}
          {guestOrderOverlay}
          {guestConfirmedEmail != null && (
            <OrderConfirmed
              email={guestConfirmedEmail}
              jobNumber={guestConfirmedRef}
              onCreateAccount={() => {
                trackSketchplan('sketchplan_post_purchase_signup_started');
                setLoginPrefillEmail(guestConfirmedEmail);
                setAuthMode('signup');
                setGuestConfirmedEmail(null);
                setShowLoginModal(true);
              }}
              onSignIn={() => {
                setLoginPrefillEmail(guestConfirmedEmail);
                setAuthMode('login');
                setGuestConfirmedEmail(null);
                setShowLoginModal(true);
              }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-black min-h-screen">
      <Header 
        onReset={handleReset} 
        showNewPhoto={appState !== AppState.UPLOAD && appState !== AppState.INITIALIZING}
        user={userProfile}
        onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
        onSignUp={() => { setAuthMode('signup'); setShowLoginModal(true); }}
        onOrder={() => navigateOrder()}
        onOpenDashboard={handleOpenDashboard}
        onOpenBuyCredits={() => setShowBuyCreditsModal(true)}
        onNavigateToSettings={handleOpenDashboard}
        onNavigateToMembers={handleOpenDashboard}
        onNavigateToReferrals={() => setShowReferrals(true)}
        isAdmin={isAdmin}
        onOpenAdmin={() => setShowAdminPanel(true)}
        onOpenBranding={() => setShowBrandingModal(true)}
      />
      {/* Scrollable column: main content + footer so footer sits below upload panel (scroll to see it) */}
      <div className="flex-1 min-h-0 flex flex-col">
      <main
        className={
          appState === AppState.UPLOAD
            ? "flex-1 min-h-0 overflow-y-auto relative"
            : "flex-1 min-h-[calc(100vh-4rem)] overflow-hidden relative"
        }
      >
        {appState === AppState.INITIALIZING && (
           <div className="flex items-center justify-center h-full">
             <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
           </div>
        )}

        {appState === AppState.UPLOAD && (
          <>
            <FluidBackground />
            <div className="relative z-10 w-full px-4 py-8">
              <div className="w-full max-w-6xl mx-auto space-y-8">
                <UploadZone onImagesSelected={handleImagesSelected} />
                <DirectCardPricingTable />
              </div>
            </div>
            <div className="relative z-10 shrink-0">
              <Footer />
            </div>
          </>
        )}

        {appState === AppState.MODE_SELECTION && images.originalUrl && (
          <ModeSelection
            imageUrl={images.originalUrl}
            onSelectMode={handleModeSelected}
            onBack={handleReset}
          />
        )}

        {appState === AppState.PROCESSING && images.originalUrl && !images.maskDataUrl ? (
            <div className="flex flex-col items-center justify-center h-full gap-8 bg-black animate-in fade-in duration-500">
                 <div className="relative">
                     <div className="absolute inset-0 bg-teal-500/20 blur-xl rounded-full animate-pulse"></div>
                     <Loader2 className="w-16 h-16 text-teal-500 animate-spin relative z-10" />
                 </div>
                 <div className="text-center space-y-2">
                     <h2 className="text-2xl font-bold text-slate-100">Converting Your Floor Plan</h2>
                     <p className="text-slate-400 max-w-sm">
                         Our AI is transforming your sketch into a professional floor plan. 
                         This usually takes 20-30 seconds.
                     </p>
                 </div>
            </div>
        ) : (
            (appState === AppState.EDITOR || appState === AppState.PROCESSING) && images.originalUrl && (
              <>
                <Editor 
                  imageUrl={images.originalUrl} 
                  onProcess={handleProcess}
                  isProcessing={appState === AppState.PROCESSING}
                  onDownload={downloadCurrentOriginal}
                  onOpenDashboard={handleOpenDashboard}
                  styleReferenceDataUrl={brandingProfile.styleReferenceDataUrl}
                />
                {error && (
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-6 py-3 rounded-full shadow-lg font-medium animate-bounce border border-red-400">
                    {error}
                  </div>
                )}
                {isUploadingOriginal && (
                    <div className="absolute bottom-6 right-6 z-50 bg-slate-800/80 text-slate-300 px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 backdrop-blur-md border border-slate-700">
                        <Loader2 size={12} className="animate-spin" />
                        Saving to cloud...
                    </div>
                )}
              </>
            )
        )}

      </main>

      {/* Footer outside main for non-upload states; upload state renders footer inside scrollable main */}
      {!showDashboard && appState !== AppState.UPLOAD && (
        <div className="shrink-0">
          <Footer />
        </div>
      )}
      </div>

      {/* Result Viewer - rendered as overlay to ensure visibility */}
      {appState === AppState.RESULT && (
        (images.processedUrl || vectorData) ? (
          <div className="fixed inset-0 z-40 bg-black">
            <ResultViewer 
              originalUrl={images.originalUrl || images.processedUrl || ''}
              processedUrl={images.processedUrl || ''}
              onBack={() => setAppState(AppState.UPLOAD)}
              onAccept={handleAccept}
              onDownload={handleDownload}
              onRequestAmendment={handleAmendment}
              mode={selectedMode}
              vectorData={vectorData}
              userCredits={userProfile?.credits || 0}
              downloadCreditCost={2}
              onConsumeCredits={handleConsumeCreditsForDownload}
              onOrderPro={handleOrderPro}
              isLoggedIn={!!session}
              brandingProfile={brandingProfile}
              onOpenBranding={() => setShowBrandingModal(true)}
            />
          </div>
        ) : (
          <div className="fixed inset-0 z-40 bg-black flex flex-col items-center justify-center gap-4">
            <p className="text-red-400 text-lg">Error: Result image not found</p>
            <p className="text-slate-400 text-sm">The processed image was not saved correctly.</p>
            <button 
              onClick={() => {
                setAppState(AppState.UPLOAD);
                setShowDashboard(true);
              }}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-500 font-medium"
            >
              Go to Gallery
            </button>
          </div>
        )
      )}

      {showLoginModal && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="w-full max-w-lg">
                <Login
                  onClose={() => setShowLoginModal(false)}
                  initialMode={authMode}
                  initialEmail={loginPrefillEmail}
                />
              </div>
          </div>
      )}

      {/* Animated Login Prompt for non-logged-in users who upload */}
      {showLoginPrompt && (
          <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
              <div className="relative w-full max-w-md animate-in zoom-in-95 fade-in duration-300">
                  {/* Close button */}
                  <button 
                    onClick={() => { setShowLoginPrompt(false); setPendingFile(null); }}
                    className="absolute -top-2 -right-2 z-10 p-2 bg-slate-800 rounded-full text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                  >
                    <X size={20} />
                  </button>
                  
                  {/* Card */}
                  <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-8 border border-slate-700 shadow-2xl">
                      {/* Logo */}
                      <div className="flex justify-center mb-6">
                          <div className="relative">
                              <div className="absolute inset-0 bg-teal-500/20 blur-2xl rounded-full animate-pulse"></div>
                              <img 
                                src="/cropped-Sketchplan-Logo1-removebg-preview.webp" 
                                alt="Sketchplan" 
                                className="relative h-16 w-auto drop-shadow-lg"
                              />
                          </div>
                      </div>
                      
                      {/* Image preview thumbnail */}
                      {pendingFile && (
                          <div className="flex justify-center mb-4">
                              <div className="relative w-20 h-20 rounded-xl overflow-hidden border-2 border-slate-600 shadow-lg">
                                  <img 
                                    src={URL.createObjectURL(pendingFile)} 
                                    alt="Uploaded" 
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                  <ImageIcon className="absolute bottom-1 right-1 w-4 h-4 text-white/70" />
                              </div>
                          </div>
                      )}
                      
                      {/* Text */}
                      <div className="text-center mb-8">
                          <h2 className="text-2xl font-bold text-white mb-2">
                              Ready to Convert!
                          </h2>
                          <p className="text-slate-400">
                              Sign in to transform your sketch into a professional floor plan.
                          </p>
                          <p className="text-teal-400 text-sm mt-2 font-medium">
                              ✨ Get 10 free credits when you sign up!
                          </p>
                      </div>
                      
                      {/* Buttons */}
                      <div className="space-y-3">
                          <button
                            onClick={() => handleLoginPromptAuth('signup')}
                            className="w-full py-4 px-6 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white font-semibold rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 hover:scale-[1.02] active:scale-[0.98]"
                          >
                            <UserPlus size={20} />
                            Create Free Account
                          </button>
                          
                          <button
                            onClick={() => handleLoginPromptAuth('login')}
                            className="w-full py-4 px-6 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98]"
                          >
                            <LogIn size={20} />
                            Sign In
                          </button>
                      </div>
                      
                      {/* Fine print */}
                      <p className="text-center text-slate-500 text-xs mt-6">
                          Your image is saved and will be ready after you sign in.
                      </p>
                  </div>
              </div>
          </div>
      )}

      {showDashboard && userProfile && (
          <Dashboard 
             user={userProfile} 
             onClose={() => {
               setShowDashboard(false);
               setDashboardInitialTab(undefined);
               setDashboardInitialCreditsAmount(undefined);
             }} 
             onLogout={handleLogout}
             onOpenEditor={handleImageSelected}
             onConsumeCredit={consumeCredit}
             onContinueEditing={handleContinueEditing}
             currentApp="sketchplan"
             initialBillingTab={dashboardInitialTab}
             initialCreditsAmount={dashboardInitialCreditsAmount}
          />
      )}

      {showBuyCreditsModal && userProfile && (
          <BuyCreditsModal user={userProfile} onClose={() => setShowBuyCreditsModal(false)} />
      )}

      {showPricingBenefitsModal && (
        <PricingBenefitsModal
          onClose={() => setShowPricingBenefitsModal(false)}
          showCredits
        />
      )}

      {showReferrals && userProfile && (
          <ReferralsPage userId={userProfile.id} onClose={() => setShowReferrals(false)} />
      )}

      <ReferralPromoPopup
          client={supabase}
          userId={userProfile?.id}
          appSource="sketchplan"
          onInvite={() => setShowReferrals(true)}
          suppressed={showReferrals || showBuyCreditsModal || showAdminPanel}
      />

      {/* Admin Panel */}
      {showAdminPanel && userProfile && isAdmin && adminRole && (
          <AdminPanel 
             user={userProfile} 
             adminRole={adminRole}
             onClose={() => setShowAdminPanel(false)} 
          />
      )}

      {/* Admin Button - Floating on Dashboard */}
      {showDashboard && userProfile && isAdmin && !showAdminPanel && (
          <button
            onClick={() => setShowAdminPanel(true)}
            className="fixed bottom-20 right-4 z-[9999] bg-teal-600 hover:bg-teal-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/></svg>
            Admin Panel
          </button>
      )}
      
      {/* Pro Order Modal */}
      {guestOrderOverlay}
      {guestConfirmedEmail != null && (
        <OrderConfirmed
          email={guestConfirmedEmail}
          jobNumber={guestConfirmedRef}
          onCreateAccount={() => {
            trackSketchplan('sketchplan_post_purchase_signup_started');
            setLoginPrefillEmail(guestConfirmedEmail);
            setAuthMode('signup');
            setGuestConfirmedEmail(null);
            setShowLoginModal(true);
          }}
          onSignIn={() => {
            setLoginPrefillEmail(guestConfirmedEmail);
            setAuthMode('login');
            setGuestConfirmedEmail(null);
            setShowLoginModal(true);
          }}
        />
      )}
      <ProOrderModal
        isOpen={showProOrderModal}
        onClose={() => {
          setShowProOrderModal(false);
          setProOrderSketchUrls([]);
          setProOrderSketchStorageUrls([]);
          setProOrderSketchFiles([]);
          setIsUploadingProSketch(false);
          setUploadProgress(0);
        }}
        sketchUrls={proOrderSketchUrls}
        sketchStorageUrls={proOrderSketchStorageUrls}
        isUploadingSketch={isUploadingProSketch}
        uploadProgress={uploadProgress}
        userEmail={session?.user?.email || ''}
        userCredits={userProfile?.credits || 0}
        billingMode={userProfile?.billing_mode === 'monthly_invoice' ? 'monthly_invoice' : 'prepaid'}
        customPlanRatePence={userProfile?.custom_plan_rate_pence ?? null}
        onSubmit={handleSubmitProOrder}
        onTopUp={(creditsNeeded) => {
          setDashboardInitialCreditsAmount(creditsNeeded);
          setDashboardInitialTab('topup');
          setShowDashboard(true);
        }}
        brandingProfile={brandingProfile}
        onOpenBranding={() => setShowBrandingModal(true)}
      />

      {/* Card payment result (after returning from Stripe Checkout) */}
      {proOrderPaymentResult && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-slate-700/50 text-center">
            {proOrderPaymentResult === 'success' ? (
              <>
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Payment received!</h3>
                <p className="text-slate-400 mb-6 leading-relaxed">
                  Thanks — your floor plan order is confirmed and our team is on it. We'll email your completed plan within your chosen turnaround.
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-6">
                  <AlertCircle size={40} className="text-amber-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Payment cancelled</h3>
                <p className="text-slate-400 mb-6 leading-relaxed">
                  No payment was taken and your order hasn't been placed. You can try again whenever you're ready.
                </p>
              </>
            )}
            <button
              onClick={() => setProOrderPaymentResult(null)}
              className="w-full py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 transition-all"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Branding Modal */}
      <BrandingModal
        isOpen={showBrandingModal}
        onClose={() => setShowBrandingModal(false)}
        profile={brandingProfile}
        onChange={handleBrandingChange}
      />

      {/* Version Footer */}
      <div className="fixed bottom-2 right-2 text-[10px] text-slate-600 z-10">
        v{APP_VERSION}
      </div>
      
    </div>
  );
}

export default App;